<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[80vw]"
    @close="emit('update:modelValue', false)"
  >
    <template #header>
      <h2 class="text-xl font-bold text-textPrimary text-left">Detalhes da Conciliação</h2>
    </template>

    <div class="flex flex-col gap-6 text-left">
      <div v-if="preConfirmedRows.length > 0" class="flex flex-col gap-3">
        <p class="text-base font-bold text-textPrimary">Linhas conciliadas automaticamente</p>
        <div
          v-for="row in preConfirmedRows"
          :key="row.entryId"
          data-testid="pre-confirmed-conciliation-row"
          class="flex items-center justify-between gap-3 border border-lightGray30 rounded-xl p-3"
        >
          <div class="flex items-center gap-3 min-w-0">
            <span
              class="text-xs font-bold text-positive bg-positive/10 rounded-full px-2 py-1 shrink-0"
            >
              Conciliado automaticamente
            </span>
            <span class="text-sm text-textPrimary truncate">
              {{ buildPreConfirmedLabel(row) }}
            </span>
          </div>
          <OutlineButton
            text="Desfazer"
            data-testid="undo-automatic-conciliation-button"
            width="96px"
            height="32px"
            class="shrink-0"
            :disabled="undoingEntryId === row.entryId"
            @click="emit('undo', row.entryId)"
          />
        </div>
      </div>

      <div v-if="currentRow" class="flex flex-col gap-6">
        <div>
          <p class="text-base font-bold text-textPrimary mb-3">Informações da folha</p>
          <DsAttendanceInfoSheetsCard
            :items="[currentAttendanceInfo]"
            :hide-signature-columns="hideSignatureColumns"
          />
        </div>

        <div v-if="currentRow.matchStatus === 'SINGLE_MATCH' && currentRow.candidates.length > 0">
          <p class="text-base font-bold text-textPrimary mb-3">Melhor candidato</p>
          <div class="flex flex-col lg:flex-row gap-6">
            <div class="w-full lg:w-1/2 min-w-0">
              <CardBestCandidate
                v-bind="mapCandidateToCardProps(currentRow.candidates[0])"
                :observations="formattedObservations"
                active
              />
            </div>

            <div class="lg:w-56 flex flex-col gap-3">
              <p class="text-base text-textPrimary">
                Essa vaga corresponde com a linha indicada do registro?
              </p>
              <v-radio-group v-model="singleMatchAnswer" hide-details>
                <v-radio label="Sim" value="yes" color="primary" />
                <v-radio label="Não" value="no" color="primary" />
              </v-radio-group>
            </div>
          </div>
        </div>

        <div v-else-if="currentRow.matchStatus === 'MULTIPLE_MATCHES'">
          <p class="text-sm text-textPrimary mb-4">
            {{
              currentRow.matchMessage ??
              'Vários candidatos encontrados. Selecione um para continuar a conciliação.'
            }}
          </p>

          <v-radio-group v-model="currentMultipleSelection" hide-details class="mb-4">
            <v-radio
              label="Nenhum dos candidatos abaixo corresponde à linha do registro"
              value="none"
            />
          </v-radio-group>

          <p class="text-base font-bold text-textPrimary mb-3">Melhores candidatos:</p>
          <div
            :class="
              currentRow.candidates.length <= 2
                ? 'grid grid-cols-2 gap-4'
                : 'flex gap-4 overflow-x-auto pb-2'
            "
          >
            <div
              v-for="(candidate, index) in currentRow.candidates"
              :key="index"
              :data-testid="`conciliation-candidate-${index}`"
              class="flex items-start gap-2 cursor-pointer"
              :class="currentRow.candidates.length > 2 ? 'min-w-[380px] max-w-[420px]' : ''"
              @click="currentMultipleSelection = String(index)"
            >
              <v-icon
                :icon="
                  currentMultipleSelection === String(index)
                    ? mdiCheckboxMarked
                    : mdiCheckboxBlankOutline
                "
                :color="currentMultipleSelection === String(index) ? 'primary' : '#BCBCCD'"
                size="22"
                class="mt-1 shrink-0"
              />
              <div class="flex flex-col gap-1 min-w-0 flex-1">
                <span
                  v-if="candidate.isRecommended"
                  data-testid="conciliation-candidate-recommended-badge"
                  class="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-1 shrink-0 self-start"
                >
                  Sugerido
                </span>
                <CardBestCandidate
                  v-bind="mapCandidateToCardProps(candidate)"
                  :observations="
                    currentMultipleSelection === String(index) ? formattedObservations : []
                  "
                  :active="currentMultipleSelection === String(index)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="currentRow.matchStatus === 'NO_MATCH'"
          class="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4"
        >
          <v-icon :icon="mdiAlertCircleOutline" color="red" size="24" class="shrink-0 mt-0.5" />
          <div>
            <p class="text-sm font-bold text-red-700 mb-1">Nenhum plantão encontrado</p>
            <p class="text-sm text-red-600">
              Não foi possível encontrar um plantão correspondente para este registro. Você pode
              avançar sem conciliar esta linha.
            </p>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span v-if="rows.length > 0" class="text-sm text-neutral50">
          Conciliação de vaga {{ currentIndex + 1 }}/{{ rows.length }}
        </span>
        <span v-else class="text-sm text-neutral50">
          Nenhuma linha pendente de revisão manual
        </span>
        <div class="flex flex-col sm:flex-row gap-3">
          <OutlineButton
            text="Voltar"
            class="w-full sm:w-auto"
            width="96px"
            height="40px"
            @click="emit('update:modelValue', false)"
          />
          <FilledButton
            :text="isLastSlot ? 'Concluir' : 'Próxima vaga'"
            class="w-full sm:w-auto bg-primary30"
            width="140px"
            height="40px"
            :disabled="isNextDisabled || (isLastSlot && isSubmitting)"
            @click="handleNext"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { addHours, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { mdiCheckboxMarked, mdiCheckboxBlankOutline, mdiAlertCircleOutline } from '@mdi/js'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import DsAttendanceInfoSheetsCard from '@/components/designSystem/Cards/DsAttendanceInfoSheetsCard.vue'
import type { DsAttendanceInfoRow } from '@/components/designSystem/Cards/DsAttendanceInfoSheetsCard.vue'
import CardBestCandidate from '@/components/designSystem/Cards/CardBestCandidate.vue'
import NetworkService from '@/Network/index'
import type { DoctorStationObservationListResponse } from '@/Network/Types/Responses/DsObservationResponse'
import { mapCandidateToCardProps } from '../utils/conciliation-candidate'
import type { DSAttendanceConciliateRowResult } from '@/Network/Types/Responses/DsAttendancePolicyResponses'

const NOT_INFORMED = 'Não informado'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    /** QW-01/28-06 — só as linhas NÃO pré-confirmadas (fila de perguntas Sim/Não). */
    rows: DSAttendanceConciliateRowResult[]
    /** QW-01/28-06 — linhas já `CONCILIATED`+`AUTOMATIC` na resposta de `/conciliate`, exibidas
     * com badge + "Desfazer" ao invés de entrar na fila de perguntas. */
    preConfirmedRows?: DSAttendanceConciliateRowResult[]
    /** entryId em processamento no "Desfazer" (desabilita o botão daquela linha). */
    undoingEntryId?: number | null
    sheetRows: Record<string, string | boolean>[]
    confirmed?: boolean
    hideSignatureColumns?: boolean
  }>(),
  {
    rows: () => [],
    preConfirmedRows: () => [],
    undoingEntryId: null,
    sheetRows: () => [],
    confirmed: false,
    hideSignatureColumns: false
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: [selections: Record<number, number>]
  /** QW-01/28-06 — "Desfazer" numa linha pré-confirmada; o pai chama confirm-conciliation
   * com validationStatus=DIVERGENT para aquela entry isolada. */
  undo: [entryId: number]
}>()

/**
 * BUG-02/QW-02 — o backend já ranqueia os candidatos de uma linha MULTIPLE_MATCHES e marca
 * `isRecommended` no melhor (proximidade de checkin/checkout). Pré-selecionar aqui só
 * materializa a recomendação na tela: o auditor continua livre para trocar antes de
 * confirmar — nenhuma seleção é enviada sem passar por "Próxima vaga"/"Concluir".
 */
function preselectRecommendedCandidate(row: DSAttendanceConciliateRowResult | null): string | null {
  if (!row || row.matchStatus !== 'MULTIPLE_MATCHES') return null
  const recommendedIndex = row.candidates.findIndex((c) => c.isRecommended)
  return recommendedIndex >= 0 ? String(recommendedIndex) : null
}

const currentIndex = ref(0)
const singleMatchAnswer = ref<'yes' | 'no' | null>(null)
const currentMultipleSelection = ref<string | null>(
  preselectRecommendedCandidate(props.rows[0] ?? null)
)
const selections = ref<Record<number, number>>({})
const isSubmitting = ref(false)

const observations = ref<DoctorStationObservationListResponse>({ data: [], count: 0 })

const currentRow = computed(() => props.rows[currentIndex.value] ?? null)
// Sem linhas pendentes (todas pré-confirmadas), o footer libera "Concluir" direto — sem isso
// o botão ficaria preso em "Próxima vaga" incrementando um índice que nunca fecha o modal.
const isLastSlot = computed(
  () => props.rows.length === 0 || currentIndex.value === props.rows.length - 1
)

const isNextDisabled = computed(() => {
  if (!currentRow.value) return false

  const matchStatus = currentRow.value.matchStatus

  if (matchStatus === 'SINGLE_MATCH' && currentRow.value.candidates.length > 0) {
    return singleMatchAnswer.value === null
  }

  if (matchStatus === 'MULTIPLE_MATCHES') {
    return currentMultipleSelection.value === null
  }

  return false
})

function formatTime(isoString: string | null | undefined): string {
  if (!isoString) return '-'
  return format(new Date(isoString), 'HH:mm')
}

function getSheetValue(
  row: Record<string, string | boolean>,
  normalizedTarget: string
): string | boolean | undefined {
  return Object.entries(row).find(([key]) => {
    const normalized = key.normalize('NFD').replace(/\p{Diacritic}/gu, '')
    return normalized === normalizedTarget
  })?.[1]
}

function extractSheetFields(rowIndex: number) {
  const sheetRow = props.sheetRows[rowIndex]
  if (!sheetRow) {
    return { time: '-', entrySignature: false, exitSignature: false }
  }
  const journeyStart = getSheetValue(sheetRow, 'jornada_inicio')
  const journeyEnd = getSheetValue(sheetRow, 'jornada_fim')
  return {
    time: journeyStart && journeyEnd ? `${journeyStart} - ${journeyEnd}` : '-',
    entrySignature: !!getSheetValue(sheetRow, 'ass._e_carimbo_entrada'),
    exitSignature: !!getSheetValue(sheetRow, 'ass._e_carimbo_saida')
  }
}

function buildAttendanceInfo(row: DSAttendanceConciliateRowResult): DsAttendanceInfoRow {
  const checkin = row.checkinReal ? new Date(row.checkinReal) : null
  const { time, entrySignature, exitSignature } = extractSheetFields(row.rowIndex)

  return {
    doctorName: row.rawDoctorName?.toUpperCase() ?? '-',
    crm: row.rawCrm ?? '-',
    date: checkin ? format(checkin, 'dd/MM/yyyy') : '-',
    time,
    station: '-',
    entryTime: formatTime(row.checkinReal),
    entrySignature,
    exitTime: formatTime(row.checkoutReal),
    exitSignature
  }
}

const currentAttendanceInfo = computed<DsAttendanceInfoRow>(() => {
  const row = currentRow.value
  return row ? buildAttendanceInfo(row) : emptyAttendanceInfo()
})

watch(
  () => props.modelValue,
  (val) => {
    if (val && !props.confirmed) {
      currentIndex.value = 0
      singleMatchAnswer.value = null
      currentMultipleSelection.value = preselectRecommendedCandidate(props.rows[0] ?? null)
      selections.value = {}
      isSubmitting.value = false
    }
  }
)

watch(currentIndex, () => {
  if (props.confirmed) {
    const row = currentRow.value
    if (row?.matchStatus === 'SINGLE_MATCH') {
      singleMatchAnswer.value = selections.value[row.rowIndex] ? 'yes' : 'no'
    } else if (row?.matchStatus === 'MULTIPLE_MATCHES') {
      const savedId = selections.value[row.rowIndex]
      const idx = savedId ? row.candidates.findIndex((c) => c.doctorStationId === savedId) : -1
      currentMultipleSelection.value = idx >= 0 ? String(idx) : 'none'
    } else {
      singleMatchAnswer.value = null
      currentMultipleSelection.value = null
    }
  } else {
    singleMatchAnswer.value = null
    currentMultipleSelection.value = preselectRecommendedCandidate(currentRow.value)
  }
})

async function fetchObservations(doctorStationId: number) {
  try {
    observations.value = await NetworkService.observation.list(doctorStationId)
  } catch {
    observations.value = { data: [], count: 0 }
  }
}

watch(
  currentRow,
  (row) => {
    observations.value = { data: [], count: 0 }
    if (row?.matchStatus === 'SINGLE_MATCH' && row.candidates.length > 0) {
      fetchObservations(row.candidates[0].doctorStationId)
    }
  },
  { immediate: true }
)

watch(currentMultipleSelection, (sel) => {
  const row = currentRow.value
  if (!row || row.matchStatus !== 'MULTIPLE_MATCHES' || sel === null || sel === 'none') {
    observations.value = { data: [], count: 0 }
    return
  }
  const idx = Number(sel)
  if (!isNaN(idx) && row.candidates[idx]) {
    fetchObservations(row.candidates[idx].doctorStationId)
  }
})

const formattedObservations = computed(() =>
  observations.value.data.map((obs) => {
    let correctedTimestamp = 'Data inválida'
    if (obs.createdAt) {
      try {
        const utcDate = new Date(obs.createdAt)
        const timezone = obs.doctorStation?.timezone || '-03:00'
        const offsetHours = parseInt(timezone.split(':')[0], 10)
        const localDate = addHours(utcDate, offsetHours)
        correctedTimestamp = format(localDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
      } catch {
        correctedTimestamp = 'Data inválida'
      }
    }
    return {
      author: obs.author.name,
      timestamp: correctedTimestamp,
      text: obs.text
    }
  })
)

function emptyAttendanceInfo(): DsAttendanceInfoRow {
  return {
    doctorName: '-',
    crm: '-',
    date: '-',
    time: '-',
    station: '-',
    entryTime: '-',
    entrySignature: false,
    exitTime: '-',
    exitSignature: false
  }
}

/** QW-01/28-06 — rótulo do médico + vaga casada para a linha pré-confirmada (badge). */
function buildPreConfirmedLabel(row: DSAttendanceConciliateRowResult): string {
  const candidate = row.candidates.find((c) => c.doctorStationId === row.doctorStationId)
  const doctorName = row.rawDoctorName?.toUpperCase() ?? NOT_INFORMED
  if (!candidate) {
    return `${doctorName} — Vaga #${row.doctorStationId ?? '-'}`
  }
  const breadcrumb = [
    candidate.company?.name,
    candidate.healthUnit?.name,
    candidate.sector?.name,
    candidate.position?.name
  ]
    .filter(Boolean)
    .join(' > ')
  return `${doctorName} — ${breadcrumb}`
}

function accumulateCurrentSelection() {
  const row = currentRow.value
  if (!row) return

  if (row.matchStatus === 'SINGLE_MATCH' && row.candidates.length > 0) {
    if (singleMatchAnswer.value === 'yes') {
      selections.value[row.rowIndex] = row.candidates[0].doctorStationId
    } else {
      delete selections.value[row.rowIndex]
    }
  } else if (row.matchStatus === 'MULTIPLE_MATCHES') {
    if (currentMultipleSelection.value === 'none') {
      delete selections.value[row.rowIndex]
    } else {
      const idx = Number(currentMultipleSelection.value)
      if (!isNaN(idx) && row.candidates[idx]) {
        selections.value[row.rowIndex] = row.candidates[idx].doctorStationId
      }
    }
  }
}

function handleNext() {
  accumulateCurrentSelection()
  if (isLastSlot.value) {
    if (isSubmitting.value) return
    isSubmitting.value = true
    emit('confirm', { ...selections.value })
    emit('update:modelValue', false)
  } else {
    currentIndex.value++
  }
}
</script>
